import { useUserEventLogger } from '@repo/tracking-service';
import { Description } from '@zenterprise-inc/ui';
import cn from 'clsx';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';

import { businessSmallBossMachineOwnerQuery, YesNoBlank } from '@/__generated__/businessSmallBossMachineOwnerQuery.graphql';
import { SurveyTopNavigation } from '@/components/survey/common/TopNavigation';
import { useSurveyUserEventAttributes } from '@/components/survey/common/hooks/use-survey-user-event-attributes';
import { AnswerList, CTA, ProgressBar } from '@/components/survey/common/ui';
import common from '@/components/survey/common/ui/styles/common.module.scss';
import { upsertSmallBossOrMachineOwner } from '@/graphql/mutation/survey/business';
import { smallBossMachineOwnerQuery } from '@/graphql/query/survey/business';
import { SURVEY_PAGES } from '@/lib/constants/paths';
import { useUpsertBusinessAnswer } from '@/lib/hooks/api/mutation/survey/use-upsert-business-answer';

const answers: { value: YesNoBlank; label: string }[] = [
  { value: 'no', label: '아니요, 둘다 해당하지 않아요' },
  { value: 'yes', label: '소사장제 / 지입사예요' }
];

export const SmallBossMachineOwnerContainer = ({ queryRef }: { queryRef: PreloadedQuery<businessSmallBossMachineOwnerQuery> }) => {
  const router = useRouter();
  const { sendClickEvent } = useUserEventLogger();
  const eventAttributes = useSurveyUserEventAttributes();

  const { getSurveyBusinessAnswer } = usePreloadedQuery<businessSmallBossMachineOwnerQuery>(smallBossMachineOwnerQuery, queryRef);
  const [upsertBusinessAnswer, isLoading] = useUpsertBusinessAnswer(upsertSmallBossOrMachineOwner);

  const [selectedAnswer, setSelectedAnswer] = useState<YesNoBlank>('blank');

  useEffect(() => {
    if (getSurveyBusinessAnswer.result && getSurveyBusinessAnswer.businessAnswer) {
      setSelectedAnswer(getSurveyBusinessAnswer.businessAnswer?.[0]?.isSmallBossOrMachineOwner || 'blank');
    }
  }, [getSurveyBusinessAnswer.businessAnswer, getSurveyBusinessAnswer.result]);

  return (
    <>
      <SurveyTopNavigation leftClickEvent={'survey_small-boss-machine-owner_back'} />
      <main className={cn(common.main_layout, common.main_cta_space)}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            sendClickEvent('survey_small-boss-machine-owner_confirm', eventAttributes);

            upsertBusinessAnswer(
              { isSmallBossOrMachineOwner: selectedAnswer },
              {
                onCompleted: () => {
                  router.push(SURVEY_PAGES.새간판);
                }
              }
            );
          }}
        >
          <article>
            <ProgressBar step={1} />

            <section>
              <h1 className={common.content_title}>
                이 사업이
                <br />
                소사장제나 지입사인가요?
              </h1>

              <AnswerList
                answers={answers}
                checkedValue={selectedAnswer}
                onChange={(value) => {
                  sendClickEvent(`survey_small-boss-machine-owner${value}`, eventAttributes);

                  setSelectedAnswer(value);
                }}
              />
            </section>
            <div className={'mt-3'}>
              <Description.Feedback
                hasStatusIcon
                hasLinkIcon={false}
                hasBg={false}
                status={'info'}
                text={'소사장제란, 근로자가 아닌 자가 사업장 내 생산라인 등 공정의 일부를 도급 또는 위탁을 받아 경영하는 것을 말해요.'}
              />
              <Description.Feedback
                hasStatusIcon
                hasLinkIcon={false}
                hasBg={false}
                status={'info'}
                text={'지입사란, 운수회사를 통해 본인의 차로 일반화물운송업에 취업하는 것을 말해요.'}
              />
            </div>
          </article>

          {selectedAnswer !== 'blank' && (
            <CTA type={'submit'} disabled={isLoading}>
              다음
            </CTA>
          )}
        </form>
      </main>
    </>
  );
};
